import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ProfileTypes } from '@api/Users/User';
import useOpenModal from '@hooks/useOpenModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';

// Iconos
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import KioskIcon from '@mui/icons-material/ShopTwo';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CustomButton from '@components/Utilities/Buttons/CustomButton';
import TableKioskService from '@api/TablesKiosks/TableKioskCtrl';
import { KioskStatus, TableKiosk, TableKioskType, TableStatus } from '@api/TablesKiosks/TableKiosk';
import { RootReducer } from '@store/index';
import { ModalSaveTableKiosk } from '@components/Modals/ModalSaveTableKiosk';
import QRCode from 'react-qr-code';
import IOSSwitch from '@components/Utilities/Switch/IOSSwitch';
import { InsertLink } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { playNotificationSound } from '@utils/Sounds';
import CloseIcon from '@mui/icons-material/Close';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import OrdersService from '@api/Orders/OrderCtrl';
import { isOrderLineV2 } from '@api/Orders/OrderLine';

const ListTablesKiosksScreen: React.FunctionComponent = () => {
    const navigation = useNavigate();
    const { branchOfficeSelected, profileSelected } = useSelector((state: RootReducer) => state.auth);
    const tableKioskService = useMemo(() => new TableKioskService(), []);
    const modalSaveTableKiosk = useOpenModal();

    // Estados de Datos
    const [tables, setTables] = useState<TableKiosk[]>([]);
    const [kiosks, setKiosks] = useState<TableKiosk[]>([]);
    const [filteredTables, setFilteredTables] = useState<TableKiosk[]>([]);
    const [filteredKiosks, setFilteredKiosks] = useState<TableKiosk[]>([]);
    const [currentTableKiosk, setCurrentTableKiosk] = useState<TableKiosk | undefined>(undefined);
    // En un ref, no en useState: el cleanup debe cerrar el listener VIVO, no el
    // del render anterior (con useState se fugaba uno cada vez que se salía).
    const unsubscribeRef = useRef<(() => void) | undefined>(undefined);

    // Mesas que ya pidieron la cuenta. En un ref porque lo lee el callback del
    // listener: como estado, el callback veía siempre el Set inicial (vacío) y
    // el sonido se repetía en cada snapshot mientras hubiera una cuenta pedida.
    const paymentRequestedTablesRef = useRef<Set<string>>(new Set());

    // Filtros
    const [filters] = useState({ search: '' });

    // Estado para QR Modal
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [selectedQrLink, setSelectedQrLink] = useState('');
    const [selectedTableName, setSelectedTableName] = useState('');

    const getStatusColor = (status: string) => {
        switch (status) {
            case TableStatus.AVAILABLE:
                return 'success';
            case TableStatus.ORDER_OPEN:
                return 'warning';
            case TableStatus.PAYMENT_REQUESTED:
                return 'error';
            case KioskStatus.ACTIVE:
                return 'success';
            default:
                return 'default';
        }
    };

    const getStatusText = (item: TableKiosk) => {
        if (item.type === TableKioskType.TABLE) {
            switch (item.tableStatus) {
                case TableStatus.AVAILABLE:
                    return 'Disponible ahora';
                case TableStatus.ORDER_OPEN:
                    return 'Pedido abierto';
                case TableStatus.PAYMENT_REQUESTED:
                    return 'Pago solicitado';
                default:
                    return 'Desc.';
            }
        }
        return item.kioskStatus === KioskStatus.ACTIVE ? 'Activo' : 'Inactivo';
    };

    // Función para reproducir sonido de notificación
    const playPaymentRequestedSound = () => {
        playNotificationSound();
    };

    // --- Lógica de Datos en Tiempo Real ---
    useEffect(() => {
        if (branchOfficeSelected?.id) {
            // Limpiar suscripción anterior si existe
            unsubscribeRef.current?.();
            unsubscribeRef.current = undefined;

            // Configurar listener en tiempo real
            const unsubscribeListener = tableKioskService.getTablesKiosksRealtime(
                branchOfficeSelected.id,
                (data: TableKiosk[]) => {
                    // Detectar nuevas mesas que solicitan pago
                    const newPaymentRequests = new Set<string>();
                    data.forEach((item) => {
                        if (
                            item.type === TableKioskType.TABLE &&
                            item.tableStatus === TableStatus.PAYMENT_REQUESTED &&
                            !paymentRequestedTablesRef.current.has(item.id || '')
                        ) {
                            newPaymentRequests.add(item.id || '');
                        }
                    });

                    // Si hay nuevas solicitudes de pago, reproducir sonido
                    if (newPaymentRequests.size > 0) {
                        playPaymentRequestedSound();
                    }

                    // Actualizar el set de mesas que han solicitado pago
                    const updatedPaymentRequests = new Set(paymentRequestedTablesRef.current);
                    data.forEach((item) => {
                        if (item.type === TableKioskType.TABLE && item.tableStatus === TableStatus.PAYMENT_REQUESTED) {
                            updatedPaymentRequests.add(item.id || '');
                        } else {
                            updatedPaymentRequests.delete(item.id || '');
                        }
                    });
                    paymentRequestedTablesRef.current = updatedPaymentRequests;

                    // Separar mesas y kioscos
                    const tablesData = data.filter((item) => item.type === TableKioskType.TABLE);
                    const kiosksData = data.filter((item) => item.type === TableKioskType.KIOSK);

                    setTables(tablesData);
                    setKiosks(kiosksData);
                    setFilteredTables(tablesData);
                    setFilteredKiosks(kiosksData);
                }
            );

            unsubscribeRef.current = unsubscribeListener;
        }

        return () => {
            unsubscribeRef.current?.();
            unsubscribeRef.current = undefined;
        };
    }, [branchOfficeSelected?.id]);

    // Aplicar filtros de búsqueda
    useEffect(() => {
        const searchTerm = filters.search.toLowerCase();
        setFilteredTables(tables.filter((table) => table.name.toLowerCase().includes(searchTerm)));
        setFilteredKiosks(kiosks.filter((kiosk) => kiosk.name.toLowerCase().includes(searchTerm)));
    }, [filters.search, tables, kiosks]);

    // --- Acciones ---
    const onOpenModalAdd = () => {
        setCurrentTableKiosk(undefined);
        modalSaveTableKiosk.toggle();
    };

    const onAddTableKiosk = async (tableKiosk: TableKiosk) => {
        showLoader();
        try {
            await tableKioskService.saveTableKiosk(tableKiosk);
            showAlertMessage(`${tableKiosk.type === TableKioskType.TABLE ? 'Mesa' : 'Kiosco'} guardado`);
            modalSaveTableKiosk.toggle();
            // No es necesario llamar getData() porque el listener en tiempo real actualizará automáticamente
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onToggleStatus = async (id: string) => {
        showLoader();
        try {
            await tableKioskService.toggleStatus(id);
            // No es necesario llamar getData() porque el listener en tiempo real actualizará automáticamente
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onDeleteTableKiosk = async (id: string) => {
        if (!window.confirm('¿Estás seguro de eliminar este elemento?')) return;

        showLoader();
        try {
            await tableKioskService.deleteTableKiosk(id);
            showAlertMessage('Eliminado correctamente');
            // No es necesario llamar getData() porque el listener en tiempo real actualizará automáticamente
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    // --- Lógica Específica de Mesas ---
    const onStartOrder = (_table: TableKiosk) => {
        // El POS vive en rutas distintas por perfil: `pos` para admin, el
        // home para manager/colaborador (cutover F6)
        navigation(profileSelected === ProfileTypes.Admin ? '../pos' : '../home');
    };

    const onOpenKiosk = (kiosk: TableKiosk) => {
        const baseUrl = 'https://app.krispis.mx'; // Es recomendable incluir el protocolo
        const link = `${baseUrl}/kiosk?kioskId=${kiosk.id}`;
        window.open(link, '_blank');
    };

    const onShowQR = (table: TableKiosk) => {
        const baseUrl = 'app.krispis.mx';
        const link = `${baseUrl}/table?tableId=${table.id}`;
        setSelectedQrLink(link);
        setSelectedTableName(table.name);
        setQrModalOpen(true);
    };

    // Componente para cada item de mesa/kiosk
    const TableKioskItem = ({ item }: { item: TableKiosk }) => {
        const ordersService = useMemo(() => new OrdersService(), []);
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const openMenu = Boolean(anchorEl);

        const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        };
        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        const handleEdit = () => {
            handleMenuClose();
            setCurrentTableKiosk(item);
            modalSaveTableKiosk.toggle();
        };

        const handleDelete = () => {
            handleMenuClose();
            if (item.id) void onDeleteTableKiosk(item.id);
        };

        const onCloseOrdersByTable = async () => {
            showLoader();
            try {
                if (item.id) {
                    const consolidatedOrder = await ordersService.completeTableOrders(item.id);
                    // Mismo criterio que el tablero (cutover F6): el POS 2.0 vive en
                    // `pos` para admin y en el home para manager/colaborador; una
                    // cuenta con líneas v1 ya no tiene POS — se cobra desde la lista
                    const lines = (consolidatedOrder.products || []).filter(Boolean);
                    const hasV1OnlyLines = lines.length > 0 && !lines.some((line) => isOrderLineV2(line));
                    if (hasV1OnlyLines) {
                        showAlertMessage(
                            'Cuenta consolidada con productos del menú anterior — cóbrala desde la lista de pedidos'
                        );
                    } else {
                        navigation(profileSelected === ProfileTypes.Admin ? '../pos' : '../home', {
                            state: {
                                orderId: consolidatedOrder.id
                            }
                        });
                        showAlertMessage('Órdenes de mesa consolidadas exitosamente');
                    }
                }
            } catch (error: any) {
                showAlertError(error);
            }
            hideLoader();
        };

        const onConfirmCloseOrdersByTable = async () => {
            try {
                showIOSAlert({
                    title: 'Cerrar mesa y pagar',
                    message: 'Al cerrar una mesa, se asociaran todas las ordenes de la mesa.',
                    buttons: [{ text: 'Ok', onPress: onCloseOrdersByTable }]
                });
            } catch (error: any) {
                showAlertError(error);
            }
        };

        return (
            <Card variant="outlined">
                <Stack p={4} spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="h5" fontWeight={600} title={item.name}>
                            {item.name}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IOSSwitch
                                size="medium"
                                checked={item.status}
                                onChange={() => item.id && onToggleStatus(item.id)}
                            />
                            <IconButton size="small" onClick={handleMenuClick}>
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    <Box>
                        <Chip
                            label={getStatusText(item)}
                            color={getStatusColor(item.tableStatus || item.kioskStatus || '') as any}
                            size="small"
                        />
                    </Box>
                </Stack>

                <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose} PaperProps={{ elevation: 3 }}>
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Editar</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={onConfirmCloseOrdersByTable}>
                        <ListItemIcon>
                            <CloseIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Cerrar mesa</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                        <ListItemIcon>
                            <DeleteOutlineIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Eliminar</ListItemText>
                    </MenuItem>
                </Menu>

                {item.type === TableKioskType.TABLE && (
                    <Stack>
                        <Divider />
                        <Stack direction="row">
                            {item.tableStatus !== TableStatus.PAYMENT_REQUESTED && (
                                <Button fullWidth variant="text" onClick={() => onStartOrder(item)}>
                                    Comenzar pedido
                                </Button>
                            )}
                            {item.tableStatus == TableStatus.PAYMENT_REQUESTED && (
                                <Button fullWidth variant="text" onClick={onConfirmCloseOrdersByTable}>
                                    Cerrar mesa
                                </Button>
                            )}
                            <Divider orientation="vertical" flexItem />
                            <IconButton onClick={() => onShowQR(item)}>
                                <InsertLink />
                            </IconButton>
                        </Stack>
                    </Stack>
                )}

                {item.type === TableKioskType.KIOSK && (
                    <Stack>
                        <Divider />
                        <Stack direction="row">
                            <Button fullWidth variant="text" onClick={() => onOpenKiosk(item)}>
                                Abrir en este dispositivo
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Card>
        );
    };

    return (
        <Box height="100%" p={4}>
            <Stack height="100%" spacing={3}>
                {/* Header de la Página */}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            Espacios
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gestiona tus mesas y kioscos de autoservicio
                        </Typography>
                    </Box>
                    <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={onOpenModalAdd}
                        startIcon={<AddIcon />}
                        sx={{ boxShadow: 'none', borderRadius: 2 }}
                    >
                        Nuevo Espacio
                    </CustomButton>
                </Stack>

                {/* Sección Mesas */}
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                            Mesas
                        </Typography>
                        {tables.some((t) => t.tableStatus === TableStatus.PAYMENT_REQUESTED) && (
                            <Chip label="Pagos solicitados" color="error" size="small" icon={<PriorityHighIcon />} />
                        )}
                    </Stack>

                    <Grid container spacing={2}>
                        {filteredTables.map((table) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
                                <TableKioskItem item={table} />
                            </Grid>
                        ))}
                        {filteredTables.length === 0 && (
                            <Grid item xs={12}>
                                <Box
                                    textAlign="center"
                                    py={6}
                                    bgcolor="background.paper"
                                    borderRadius={4}
                                    border="1px dashed #ddd"
                                >
                                    <TableRestaurantIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="textSecondary">No hay mesas registradas</Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Sección Kioscos */}
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                            Kioscos
                        </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                        {filteredKiosks.map((kiosk) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={kiosk.id}>
                                <TableKioskItem item={kiosk} />
                            </Grid>
                        ))}
                        {filteredKiosks.length === 0 && (
                            <Grid item xs={12}>
                                <Box
                                    textAlign="center"
                                    py={6}
                                    bgcolor="background.paper"
                                    borderRadius={4}
                                    border="1px dashed #ddd"
                                >
                                    <KioskIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="textSecondary">No hay kioscos registrados</Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Modales */}
                <ModalSaveTableKiosk
                    modalSaveTableKiosk={modalSaveTableKiosk}
                    tableKiosk={currentTableKiosk}
                    onAddTableKiosk={onAddTableKiosk}
                />

                {/* Modal de QR */}
                <Dialog open={qrModalOpen} onClose={() => setQrModalOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Mesa: {selectedTableName}</DialogTitle>
                    <DialogContent>
                        <Stack alignItems="center" spacing={3} py={2}>
                            {/* Renderizado del QR */}
                            <Box p={2} border="1px solid #eee" borderRadius={2} bgcolor="white">
                                <QRCode value={selectedQrLink} size={180} viewBox={`0 0 256 256`} />
                            </Box>

                            <Typography variant="body2" textAlign="center" color="text.secondary">
                                Escanea este código para acceder a la mesa.
                            </Typography>

                            <TextField
                                fullWidth
                                size="small"
                                value={selectedQrLink}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    void navigator.clipboard.writeText(selectedQrLink);
                                                    showAlertMessage('Enlace copiado');
                                                }}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Stack>
                    </DialogContent>
                </Dialog>
            </Stack>
        </Box>
    );
};

export default ListTablesKiosksScreen;
